import Vue from 'vue'
import firebase from 'firebase/app'
import 'firebase/database'
import { uploadFileToStorage } from '@/utils/fileUploads'
import { v4 as uuid } from 'uuid'

export default {
  namespaced: true,
  state: {
    loadingSavedAssets: false,
    savedAssets: {},
    savedAssetsRef: null,
    assetUploads: {},
    searchImages: [],
    cropDialog: false
  },
  getters: {
    savedAssets (state) {
      return state.savedAssets || {}
    },
    savedImages (state, getters) {
      return Object.values(getters.savedAssets).filter(asset => {
        return asset.mimetype && asset.mimetype.startsWith('image')
      }).reverse().sort((a, b) => {
        return b.created - a.created
      })
    },
    savedVideos (state, getters) {
      return Object.values(getters.savedAssets).filter(asset => {
        return asset.mimetype && asset.mimetype.startsWith('video')
      }).reverse().sort((a, b) => {
        return b.created - a.created
      })
    },
    assetUploads (state) {
      return state.assetUploads || {}
    },
    loadingSavedAssets (state) {
      return state.loadingSavedAssets
    },
    uploadingAssets (state, getters) {
      return Object.values(getters.assetUploads).reverse()
    },
    uploadingImages (state, getters) {
      return getters.uploadingAssets.filter(asset => {
        return !!asset.asset.mimetype && asset.asset.mimetype.startsWith('image')
      })
    },
    uploadingVideos (state, getters) {
      return getters.uploadingAssets.filter(asset => {
        return !!asset.asset.mimetype && asset.asset.mimetype.startsWith('video')
      })
    }
  },
  mutations: {
    setSavedAssets (state, payload) {
      if (payload) {
        const assets = {}
        Object.keys(payload).forEach(key => {
          assets[key] = {
            ...payload[key],
            favorite: true,
            key
          }
        })
        state.savedAssets = assets
        state.loadingSavedAssets = false
      }
    },
    startedLoadingAssets (state) {
      state.loadingSavedAssets = true
    },
    setAssetUploadProgress (state, { id, asset, progress, complete, failed }) {
      if (!complete) {
        if (state.assetUploads[id]) {
          Vue.set(state.assetUploads, id, {
            ...state.assetUploads[id],
            progress,
            failed
          })
        } else {
          Vue.set(state.assetUploads, id, {
            progress,
            asset
          })
        }
      }
    },
    clearAssetUploadProgress (state, { uploadingId, savedId }) {
      if (state.assetUploads[uploadingId]) {
        Vue.set(state.savedAssets, savedId, state.assetUploads[uploadingId])
        Vue.delete(state.assetUploads, uploadingId)
      }
    },
    saveSearchImages (state, payload) {
      state.searchImages = payload
    },
    toggleCropDialog (state, payload) {
      state.cropDialog = payload
    }
  },
  actions: {
    loadSavedAssets ({ commit, state, rootState }) {
      commit('startedLoadingAssets')
      if (state.savedAssetsRef) {
        state.savedAssetsRef.off()
      } else {
        state.savedAssetsRef = firebase.database()
          .ref('favorites')
          .orderByChild('userId')
          .equalTo(rootState.auth.authUser.uid)
      }
      state.savedAssetsRef.on('value', function (snapshot) {
        const assets = snapshot.val()
        if (assets) {
          Object.keys(assets).forEach(key => {
            const asset = assets[key]
            assets[key] = {
              ...asset,
              id: asset.id || asset.imageId,
              name: asset.name || asset.imageName
            }
            if (!asset.mimetype) {
              const extensionMatch = asset.url.match(/\.(\w{3,4})\?alt/)
              if (extensionMatch && extensionMatch.length > 1) assets[key].mimetype = 'image/' + extensionMatch[1]
              else assets[key].mimetype = 'image/png'
            }
          })
        }
        commit('setSavedAssets', assets || {})
      })
    },
    async saveUserFavoriteAsset ({ commit, rootState }, payload) {
      const user = payload.user ? payload.user : rootState.auth.authUser
      const uniqueId = user.uid + '-' + payload.id

      commit('clearAssetUploadProgress', { uploadingId: payload.id, savedId: uniqueId })

      const ref = firebase
        .database()
        .ref('favorites')
        .child(uniqueId)

      const updateData = {
        userId: user.uid,
        id: payload.id,
        name: payload.name,
        url: payload.url,
        mimetype: payload.mimetype,
        path: payload.path,
        created: {
          '.sv': 'timestamp'
        }
      }

      await ref.set(updateData)
    },
    async shareBundleWithUsers ({ dispatch }, payload) {
      const images = await dispatch('uploadBundle', payload)
      payload.users.forEach(user => {
        images.forEach(image => {
          dispatch('saveUserFavoriteAsset', {
            user: user,
            id: (new Date()).getTime(),
            name: image.name,
            url: image.url
          })
        })
      })
    },
    uploadBundle (context, { bundle }) {
      const ref = firebase.storage().ref()

      return Promise.all(bundle.images.map(async (image) => {
        const child = ref.child(`/${bundle.name}/${image.name.split('.')[0]}.jpg`)
        await child.put(image.blob)
        return {
          url: await child.getDownloadURL(),
          name: image.name.split('.')[0]
        }
      }))
    },
    async uploadAsset ({ commit, rootState }, { payload: { assetFile, assetData, name, mimetype, extension, dataUrl }, progressCallback }) {
      const id = uuid()
      let fileExtension = extension
      if (!fileExtension) {
        const extensionMatch = name.match(/\.(\w{3,4})\??/)
        if (extensionMatch && extensionMatch.length > 1) fileExtension = extensionMatch[1]
        else fileExtension = 'png'
      }
      const filename = id + '.' + fileExtension

      const onProgress = (progress) => {
        commit('setAssetUploadProgress', {
          id,
          asset: {
            url: dataUrl,
            mimetype: mimetype || `image/${fileExtension}`,
            name,
            id
          },
          progress: progress.progress,
          complete: progress.progress >= 100
        })
        if (progressCallback && typeof progressCallback === 'function') {
          progressCallback(progress.progress)
        }
      }

      try {
        const file = {
          folder: `assets/${rootState.auth.authUser.uid}`,
          file: assetFile,
          fileData: assetData,
          filename: filename,
          mimetype: mimetype || `image/${fileExtension}`
        }
        const uploadResult = await uploadFileToStorage({
          file,
          progressCallback: onProgress
        })

        if (uploadResult && uploadResult.url) {
          return {
            id,
            name,
            mimetype: file.mimetype,
            url: uploadResult.url,
            path: uploadResult.ref
          }
        }
      } catch (error) {
        commit('setAssetUploadProgress', {
          id,
          failed: true
        })
      }
    },
    async uploadCroppedImage ({ dispatch }, payload) {
      const uploadResult = await dispatch('uploadAsset', {
        payload: {
          ...payload,
          assetData: payload.image
        }
      })
      await dispatch('saveUserFavoriteAsset', uploadResult)
    },
    async updateAsset ({ rootState }, { id, data }) {
      const constructedId = rootState.auth.authUser.uid + '-' + id
      await firebase
        .database()
        .ref('favorites')
        .child(constructedId)
        .update({ ...data })
    },
    async deleteAsset ({ rootState }, { id }) {
      const favoritesId = rootState.auth.authUser.uid + '-' + id
      await firebase.database().ref('favorites').child(favoritesId).transaction(asset => {
        if (asset.userId) {
          asset.originalUser = asset.userId
          asset.userId = null
        }
        return asset
      })
    }
  }
}
